import {
    AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges,
} from '@angular/core';

import { Menu } from 'app/shared/models/control/menu';
import { MenuNavigationService } from 'app/shared/services/menu-navigation/menu-navigation.service';
import {
    ancestorKeysOf, MenuKey, NO_OPEN_SCREENS, OpenScreens,
} from 'app/shared/services/menu-open-screens/open-screens.service';

/**
 * Recursive replacement for ba-main-menu-item's jQuery accordion. The expand-vs-navigate
 * DECISION is unchanged (still driven by MenuNavigationService.isNavigable, which mirrors
 * baMainMenuItem.goto()'s branch structure exactly) - only the DOM mechanism (Angular local
 * state instead of jQuery class toggling) is new.
 */
@Component({
    selector: 'module-sections-tree',
    templateUrl: './module-sections-tree.component.html',
    styleUrls: ['./module-sections-tree.component.scss'],
})
export class ModuleSectionsTreeComponent implements OnChanges, AfterViewChecked {
    @Input() items: Menu[] = [];
    @Input() openScreens: OpenScreens = NO_OPEN_SCREENS;
    @Input() pinnedPaths: string[] = [];
    @Input() depth = 0;
    /**
     * Key of the section these items hang off - the rail module's id at the root. Rows identify
     * themselves by parentKey + own id rather than by id alone, because PagesMenu ids collide
     * (see MenuKey); two same-id nodes in one module would otherwise expand and highlight as one.
     */
    @Input() parentKey: MenuKey = '';

    @Output() readonly activate = new EventEmitter<Menu>();
    @Output() readonly togglePin = new EventEmitter<{ item: Menu; event: Event }>();

    /** Sections the user has explicitly opened. */
    readonly expanded = new Set<MenuKey>();
    /**
     * Sections the user has explicitly closed. Needed as its own set rather than "not in
     * `expanded`", because a section holding an open screen is open by default - without
     * recording the close, clicking its chevron would do nothing.
     */
    readonly collapsed = new Set<MenuKey>();

    private lastActiveKey: MenuKey | null = null;
    private pendingScroll = false;

    constructor(
        private readonly navigation: MenuNavigationService,
        private readonly host: ElementRef<HTMLElement>,
    ) { }

    /**
     * Whenever the front-most window changes, the new active screen's own ancestors stop being
     * user-collapsed - the point of the auto-expand is that switching to a screen reveals it.
     * Every other manual collapse is left alone.
     */
    ngOnChanges(changes: SimpleChanges): void {
        const activeChanged = !!changes.openScreens && this.openScreens.activeKey !== this.lastActiveKey;
        if (activeChanged) {
            this.lastActiveKey = this.openScreens.activeKey;
            if (this.openScreens.activeKey) {
                ancestorKeysOf(this.openScreens.activeKey).forEach(key => this.collapsed.delete(key));
            }
        }
        // Only the root tree scrolls: it is the one instance whose host spans every level, so the
        // active row is inside it wherever it sits, and letting each nested level scroll the same
        // element as well would just repeat the work. `items` counts alongside the active screen
        // because a rail-module switch reuses this same instance with a different module's
        // sections - the active row becomes newly visible here without activeKey having moved.
        if (this.depth === 0 && (activeChanged || changes.items)) {
            this.pendingScroll = true;
        }
    }

    /**
     * Expanding the active screen's ancestors only puts its row in the DOM - in a long module it
     * can still land outside the flyout's scroll viewport. Scrolling happens here rather than in
     * ngOnChanges because the levels below this one are created during this same change-detection
     * pass, so the row does not exist yet when the inputs land; by the root's ngAfterViewChecked
     * the whole expanded chain is rendered. `nearest` leaves an already-visible row untouched.
     */
    ngAfterViewChecked(): void {
        if (!this.pendingScroll) {
            return;
        }
        this.pendingScroll = false;
        const row = this.host.nativeElement.querySelector('.mst-row__main--active');
        row?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }

    /** This row's identity within the module - see the `parentKey` input. */
    keyFor(item: Menu): MenuKey {
        return this.parentKey ? `${this.parentKey}/${item.id}` : `${item.id}`;
    }

    /**
     * Sidebar nesting is purely structural: any node with children renders as an expandable
     * group here - at every depth including the top level, collapsed until clicked or until it
     * turns out to hold an open screen (see isExpanded) -
     * even when MenuNavigationService.isNavigable() would also treat it as directly
     * clickable (e.g. legacy `lastChild:true` group nodes like "System Parameter" that used to
     * navigate to a separate Home list page). navigate()/isNavigable() themselves are untouched -
     * they still drive Home's own grid, search-result clicks, and direct URL navigation.
     */
    isBranch(item: Menu): boolean {
        return !!item.children?.length;
    }

    /**
     * Open when the user opened it, or - unless they since closed it - when ANY open screen lives
     * under it, not just the front-most one. Several screens are normally open at once across
     * different modules, and the point of this list is to show which; gating expansion on the
     * active screen alone left every other open screen's section shut, so returning to a module
     * gave no sign of what was already open inside it.
     */
    isExpanded(item: Menu): boolean {
        const key = this.keyFor(item);
        if (this.expanded.has(key)) {
            return true;
        }
        if (this.collapsed.has(key)) {
            return false;
        }
        return this.openScreens.containerKeys.has(key);
    }

    /** This screen is open in a window somewhere - possibly minimized, possibly behind others. */
    isOpen(item: Menu): boolean {
        return this.openScreens.openKeys.has(this.keyFor(item));
    }

    /** This screen is the one in front, i.e. what the user is looking at right now. */
    isActive(item: Menu): boolean {
        return this.openScreens.activeKey === this.keyFor(item);
    }

    /**
     * A section holding an open screen. Such a section expands itself, so the marker below only
     * ever shows once the user has collapsed it by hand - it is what keeps the open screen
     * findable after they have folded it away.
     */
    hasOpenScreens(item: Menu): boolean {
        return this.openScreens.containerKeys.has(this.keyFor(item));
    }

    /**
     * Whether this row shows the open-screen dot: on a screen that is open, and on a collapsed
     * section hiding one. An expanded section doesn't need it - its open screens are right there.
     */
    showsOpenDot(item: Menu): boolean {
        return this.isBranch(item)
            ? this.hasOpenScreens(item) && !this.isExpanded(item)
            : this.isOpen(item);
    }

    toggle(item: Menu): void {
        const key = this.keyFor(item);
        if (this.isExpanded(item)) {
            this.expanded.delete(key);
            this.collapsed.add(key);
        } else {
            this.collapsed.delete(key);
            this.expanded.add(key);
        }
    }

    filteredChildren(item: Menu): Menu[] {
        return this.navigation.filterChildren(item.children);
    }

    isPinned(item: Menu): boolean {
        return this.pinnedPaths.includes(item.path);
    }

    onRowClick(item: Menu): void {
        if (this.isBranch(item)) {
            this.toggle(item);
        } else {
            this.activate.emit(item);
        }
    }

    onPinClick(item: Menu, event: Event): void {
        event.stopPropagation();
        this.togglePin.emit({ item, event });
    }
}
